import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import apiManager from '../../../lib/api';
import { User, Address } from '../../../lib/api/models';
import { TARIFA_OPTIONS } from '../../../lib/utils/tarifas';
import { T, useTranslate } from '@tolgee/react';

// Asignación múltiple para clientes y jerarquía entre comerciales.
interface CommercialAssignmentProps {
  userId: string;
  selectedCommercialIds: string[];
  commissionPercentage: number;
  onCommercialChange: (commercialIds: string[]) => void;
  onCommissionChange: (commissionPercentage: number) => void;
}

const CommercialAssignmentSection = ({
  userId,
  selectedCommercialIds,
  commissionPercentage,
  onCommercialChange,
  onCommissionChange
}: CommercialAssignmentProps) => {
  const [commercials, setCommercials] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCommercials = async () => {
      setLoading(true);
      try {
        const response = await apiManager.users.getUsers({ filter: { role: 'commercial' } });
        if (response.success && response.data) {
          setCommercials(response.data);
        }
      } catch (error) {
        console.error('Error fetching commercials:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCommercials();
  }, []);

  const toggleCommercial = (commercialId: string) => {
    onCommercialChange(
      selectedCommercialIds.includes(commercialId)
        ? selectedCommercialIds.filter(id => id !== commercialId)
        : [...selectedCommercialIds, commercialId]
    );
  };

  const availableCommercials = commercials.filter(commercial => commercial.id !== userId);

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <h2 className="text-lg font-medium text-gray-900 mb-4">Comerciales asignados</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <span className="block text-sm font-medium text-gray-700 mb-1">Selecciona uno o varios comerciales</span>
          <div className="max-h-56 overflow-y-auto rounded-md border border-gray-300 divide-y divide-gray-100">
            {!loading && availableCommercials.length === 0 && (
              <p className="px-3 py-2 text-sm text-gray-500">No hay comerciales disponibles</p>
            )}
            {availableCommercials.map(commercial => (
              <label key={commercial.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  value={commercial.id}
                  checked={selectedCommercialIds.includes(commercial.id)}
                  onChange={() => toggleCommercial(commercial.id)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-gray-900">{commercial.name}</span>
                  <span className="block text-xs text-gray-500 truncate">{commercial.email}</span>
                </span>
              </label>
            ))}
          </div>
          {loading && <p className="text-xs text-gray-500 mt-1">Cargando comerciales...</p>}
          <p className="text-xs text-gray-500 mt-1">
            Todos los seleccionados recibirán las notificaciones de los pedidos relacionados.
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Porcentaje de Comisión (%)</label>
          <input
            type="number"
            min="0"
            max="100"
            step="0.5"
            value={commissionPercentage}
            onChange={(e) => onCommissionChange(Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
          <p className="text-xs text-gray-500 mt-1">Porcentaje de comisión asociado a este usuario</p>
        </div>
      </div>
      <p className="mt-4 text-xs text-gray-500">La asignación se guardará junto con el resto de los datos del usuario.</p>
    </div>
  );
};


const UserFormPage = () => {
  const { t } = useTranslate();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState<Partial<User> & { password?: string; tarifa?: number }>({
    name: '',
    email: '',
    role: 'customer',
    tarifa: 1,
    phone: '',
    nif: '',
    active: true,
    address: {
      street: '',
      city: '',
      postalCode: '',
      country: 'España'
    }
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedCommercialIds, setSelectedCommercialIds] = useState<string[]>([]);
  const [commissionPercentage, setCommissionPercentage] = useState(0);

  useEffect(() => {
    // Cargar datos del usuario si estamos en modo edición
    const fetchUser = async () => {
      if (!isEditMode) return;

      setIsLoading(true);
      try {
        const response = await apiManager.users.getUser(id);
        if (response.success && response.data) {
          setFormData(response.data);
          const multipleAssignments = (response.data.assignedCommercials || [])
            .map(commercial => typeof commercial === 'string' ? commercial : commercial.id)
            .filter(Boolean);
          const legacyAssignment = response.data.assignedCommercial;
          setSelectedCommercialIds(
            multipleAssignments.length > 0
              ? multipleAssignments.filter(commercialId => commercialId !== id)
              : [typeof legacyAssignment === 'string' ? legacyAssignment : legacyAssignment?.id]
                .filter((commercialId): commercialId is string => Boolean(commercialId) && commercialId !== id)
          );
          setCommissionPercentage(response.data.commissionPercentage || 0);
        }
      } catch (error) {
        console.error('Error al cargar datos del usuario:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, [id, isEditMode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData({
        ...formData,
        [name]: checked
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      address: {
        ...formData.address,
        [name]: value
      } as Address
    });
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name) newErrors.name = t('admin.user_form.error_name');
    if (!formData.email) newErrors.email = t('admin.user_form.error_email');
    if (!isValidEmail(formData.email || '')) newErrors.email = t('admin.user_form.error_email_invalid');
    if (!isEditMode && !formData.password) newErrors.password = t('admin.user_form.error_password');
    if (!formData.role) newErrors.role = t('admin.user_form.error_role');

    // Validar dirección si existe
    if (formData.address) {
      if (!formData.address.street) newErrors['address.street'] = t('admin.user_form.error_street');
      if (!formData.address.city) newErrors['address.city'] = t('admin.user_form.error_city');
      if (!formData.address.postalCode) newErrors['address.postalCode'] = t('admin.user_form.error_postal_code');
      if (!formData.address.country) newErrors['address.country'] = t('admin.user_form.error_country');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidEmail = (email: string): boolean => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSaving(true);

    try {
      let response;

      // Crear o actualizar el usuario
      if (isEditMode) {
        // No enviar la contraseña si está vacía en modo edición
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password;
        }

        // La asignación tiene su propio endpoint. No reenviar aquí la copia que
        // se cargó al abrir el formulario porque podría sobrescribir el cambio.
        delete updateData.assignedCommercial;
        delete updateData.assignedCommercials;
        delete updateData.commissionPercentage;

        response = await apiManager.users.updateUser(id, updateData);
      } else {
        response = await apiManager.users.createUser(formData);
      }

      if (response.success) {
        const canHaveCommercial = formData.role === 'customer'
          || formData.role === 'shop'
          || formData.role === 'distributor'
          || formData.role === 'commercial';

        if (isEditMode && canHaveCommercial) {
          const assignmentResponse = await apiManager.users.assignCommercials(
            id,
            selectedCommercialIds,
            commissionPercentage
          );

          if (!assignmentResponse.success) {
            setErrors({
              submit: assignmentResponse.error?.message || 'Error al asignar comercial'
            });
            return;
          }
        }

        // Redirigir a la lista de usuarios
        navigate('/admin/users');
      } else {
        // Extraer mensaje de error del backend (puede venir en distintos formatos)
        const res = response as any;
        let errorMsg = response.error?.message || res.message || '';
        if (res.errors && Array.isArray(res.errors)) {
          errorMsg = res.errors.map((e: any) => `${e.field}: ${e.message}`).join('. ');
        }
        setErrors({
          submit: errorMsg || t('admin.user_form.error_save')
        });
      }
    } catch (error) {
      console.error('Error al guardar el usuario:', error);
      setErrors({
        submit: t('admin.user_form.error_save')
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AdminLayout title={isEditMode ? t('admin.user_form.title_edit', 'Editar Usuario') : t('admin.user_form.title_new', 'Nuevo Usuario')}>
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-8">
          {errors.submit && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-600">
              {errors.submit}
            </div>
          )}

          {/* Información básica */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              <T keyName="admin.user_form.basic_info">Información Básica</T>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <T keyName="admin.user_form.name">Nombre</T> <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500`}
                />
                {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <T keyName="admin.user_form.email">Email</T> <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email || ''}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500`}
                />
                {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {isEditMode ? <T keyName="admin.user_form.password_edit">Contraseña (dejar vacío para mantener)</T> : <T keyName="admin.user_form.password">Contraseña</T>} {!isEditMode && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password || ''}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${errors.password ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500`}
                />
                {errors.password && <p className="mt-1 text-sm text-red-500">{errors.password}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <T keyName="admin.user_form.role">Rol</T> <span className="text-red-500">*</span>
                </label>
                <select
                  name="role"
                  value={formData.role || 'customer'}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${errors.role ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500`}
                >
                  <option value="customer">{t('admin.user_form.role_customer')}</option>
                  <option value="commercial">{t('admin.user_form.role_commercial')}</option>
                  <option value="distributor">{t('admin.user_form.role_distributor')}</option>
                  <option value="shop">{t('admin.user_form.role_shop')}</option>
                  <option value="admin">{t('admin.user_form.role_admin')}</option>
                  <option value="beeadmin">{t('admin.user_form.role_beeadmin')}</option>
                </select>
                {errors.role && <p className="mt-1 text-sm text-red-500">{errors.role}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <T keyName="admin.user_form.tariff">Tarifa de Precios</T> <span className="text-red-500">*</span>
                </label>
                <select
                  name="tarifa"
                  value={formData.tarifa || 1}
                  onChange={(e) => setFormData({
                    ...formData,
                    tarifa: parseInt(e.target.value)
                  })}
                  className={`w-full px-3 py-2 border ${errors.tarifa ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500`}
                >
                  {TARIFA_OPTIONS.map((tarifaOption) => (
                    <option key={tarifaOption.key} value={tarifaOption.value}>
                      {tarifaOption.userFormTranslationKey
                        ? t(tarifaOption.userFormTranslationKey, tarifaOption.label)
                        : tarifaOption.label}
                    </option>
                  ))}
                </select>
                {errors.tarifa && <p className="mt-1 text-sm text-red-500">{errors.tarifa}</p>}
                <p className="mt-1 text-xs text-gray-500">
                  <T keyName="admin.user_form.tariff_help">La tarifa determina qué precios verá el usuario en todos los productos</T>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descuento Personal (%)
                </label>
                <input
                  type="number"
                  name="discount"
                  min="0"
                  max="100"
                  step="1"
                  value={(formData as any).discount || 0}
                  onChange={(e) => setFormData({
                    ...formData,
                    discount: Number(e.target.value)
                  } as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Descuento aplicado en carrito y pedidos. 0 = sin descuento. Compite con descuentos globales (gana el mayor).
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descuento Pronto Pago (%)
                </label>
                <input
                  type="number"
                  name="prontoPagoDiscount"
                  min="0"
                  max="100"
                  step="0.5"
                  value={(formData as any).prontoPagoDiscount || 0}
                  onChange={(e) => setFormData({
                    ...formData,
                    prontoPagoDiscount: Number(e.target.value)
                  } as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Solo informativo. Se muestra en carrito, checkout y pedidos como precio con pronto pago.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <T keyName="admin.user_form.phone">Teléfono</T>
                </label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <T keyName="admin.user_form.nif">NIF/CIF</T>
                </label>
                <input
                  type="text"
                  name="nif"
                  value={formData.nif || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="active"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => handleChange({
                    ...e,
                    target: {
                      ...e.target,
                      name: 'active',
                      value: e.target.checked ? 'true' : 'false',
                      type: 'checkbox'
                    }
                  })}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="active" className="ml-2 block text-sm font-medium text-gray-700">
                  <T keyName="admin.user_form.active">Usuario activo</T>
                </label>
              </div>
            </div>
          </div>

          {/* Dirección */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              <T keyName="admin.user_form.address">Dirección</T>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <T keyName="admin.user_form.street">Calle</T> <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="street"
                  value={formData.address?.street || ''}
                  onChange={handleAddressChange}
                  className={`w-full px-3 py-2 border ${errors['address.street'] ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500`}
                />
                {errors['address.street'] && <p className="mt-1 text-sm text-red-500">{errors['address.street']}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <T keyName="admin.user_form.city">Ciudad</T> <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.address?.city || ''}
                  onChange={handleAddressChange}
                  className={`w-full px-3 py-2 border ${errors['address.city'] ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500`}
                />
                {errors['address.city'] && <p className="mt-1 text-sm text-red-500">{errors['address.city']}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <T keyName="admin.user_form.postal_code">Código Postal</T> <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="postalCode"
                  value={formData.address?.postalCode || ''}
                  onChange={handleAddressChange}
                  className={`w-full px-3 py-2 border ${errors['address.postalCode'] ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500`}
                />
                {errors['address.postalCode'] && <p className="mt-1 text-sm text-red-500">{errors['address.postalCode']}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <T keyName="admin.user_form.country">País</T> <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="country"
                  value={formData.address?.country || 'España'}
                  onChange={handleAddressChange}
                  className={`w-full px-3 py-2 border ${errors['address.country'] ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500`}
                />
                {errors['address.country'] && <p className="mt-1 text-sm text-red-500">{errors['address.country']}</p>}
              </div>
            </div>
          </div>

          {/* Asignación múltiple para clientes y jerarquía entre comerciales */}
          {isEditMode && (formData.role === 'customer' || formData.role === 'shop' || formData.role === 'distributor' || formData.role === 'commercial') && (
            <CommercialAssignmentSection
              userId={id}
              selectedCommercialIds={selectedCommercialIds}
              commissionPercentage={commissionPercentage}
              onCommercialChange={setSelectedCommercialIds}
              onCommissionChange={setCommissionPercentage}
            />
          )}

          {/* Botones de acción */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/admin/users')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <T keyName="admin.user_form.cancel">Cancelar</T>
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-indigo-600 text-white rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <T keyName="admin.user_form.saving">Guardando...</T>
                </>
              ) : (
                <T keyName="admin.user_form.save">Guardar Usuario</T>
              )}
            </button>
          </div>
        </form>
      )}
    </AdminLayout>
  );
};

export default UserFormPage;
